import { IDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import {
    autoTableCreationMetadata,
    getSqlParallelReadMetadataConsts,
    isolationLevelMetadata,
    maxConcurrentConnectionsMetadata,
    preCopyScriptMetadata,
    queryTimeoutMetadata,
    sqlWriteBatchSizeMetadata,
    sqlWriteBatchTimeoutMetadata
} from '../consts/metadata-consts';
import { ColumnPhysicalType, ColumnType, defaultSupportedColumnTypes, sqlBinaryPhysicalTypes } from 'app/shared/utils/column-type.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { getSqlWriteBehavior } from '../consts/metadata-consts-with-ec-deps';
import { UpdateMethod } from 'shell-models-lib';
import { getAppendOption, getSCD2Option, getUpsertOption } from 'app/registries/consts/copy-job-const';

export const sqlServerLogicalToPhysicalMapping: { [key: string]: string } = {
    int16: ColumnPhysicalType.Smallint,
    int32: ColumnPhysicalType.Int,
    int64: ColumnPhysicalType.Bigint,
    byte: ColumnPhysicalType.Tinyint,
    'byte[]': ColumnPhysicalType.Varbinary,
    // eslint-disable-next-line id-blacklist
    boolean: ColumnPhysicalType.Bit,
    // eslint-disable-next-line id-blacklist
    string: ColumnPhysicalType.Nvarchar,
    date: ColumnPhysicalType.Date,
    datetime: ColumnPhysicalType.Datetime2,
    datetimeoffset: ColumnPhysicalType.Datetimeoffset,
    decimal: ColumnPhysicalType.Decimal,
    'char[]': ColumnPhysicalType.Nvarchar,
    single: ColumnPhysicalType.Real,
    timespan: ColumnPhysicalType.Time,
    guid: ColumnPhysicalType.Uniqueidentifier,
    double: ColumnPhysicalType.Float,
    xml: ColumnPhysicalType.Xml,
    sbyte: ColumnPhysicalType.Smallint,
    uint16: ColumnPhysicalType.Int,
    uint32: ColumnPhysicalType.Bigint,
    uint64: ColumnPhysicalType.Bigint
};

export const sqlServerPhysicalToLogicalMapping: { [key: string]: string | {[key: string] : string} } = {
    bit: ColumnType.Boolean,
    tinyint: ColumnType.Byte,
    smallint: ColumnType.Int16,
    int: ColumnType.Int32,
    bigint: ColumnType.Int64,
    smalldatetime: ColumnType.DateTime,
    date: {
        default: ColumnType.DateTime,
        Parquet: ColumnType.Date,
        GoogleBigQueryObject: ColumnType.Date
    },
    datetime: ColumnType.DateTime,
    datetime2: ColumnType.DateTime,
    datetimeoffset: ColumnType.DateTimeOffset,
    time: ColumnType.TimeSpan,
    smallmoney: ColumnType.Decimal,
    money: ColumnType.Decimal,
    decimal: ColumnType.Decimal,
    numeric: ColumnType.Decimal,
    float: ColumnType.Double,
    real: ColumnType.Single,
    uniqueidentifier: ColumnType.Guid,
    sql_variant: ColumnType.Object,
    xml: ColumnType.String,
    binary: ColumnType.ByteArray,
    image: ColumnType.ByteArray,
    varbinary: ColumnType.ByteArray,
    nchar: ColumnType.String,
    char: ColumnType.String,
    nvarchar: ColumnType.String,
    varchar: ColumnType.String,
    text: ColumnType.String,
    ntext: ColumnType.String,
    rowversion: ColumnType.ByteArray
};

export const sqlServerSupportedPhysicalTypes: ColumnPhysicalType[] = [
    ColumnPhysicalType.Bit,
    ColumnPhysicalType.Tinyint,
    ColumnPhysicalType.Smallint,
    ColumnPhysicalType.Int,
    ColumnPhysicalType.Bigint,
    ColumnPhysicalType.Smalldatetime,
    ColumnPhysicalType.Date,
    ColumnPhysicalType.Datetime,
    ColumnPhysicalType.Datetime2,
    ColumnPhysicalType.Datetimeoffset,
    ColumnPhysicalType.Time,
    ColumnPhysicalType.Smallmoney,
    ColumnPhysicalType.Money,
    ColumnPhysicalType.Decimal,
    ColumnPhysicalType.Numeric,
    ColumnPhysicalType.Float,
    ColumnPhysicalType.Real,
    ColumnPhysicalType.Uniqueidentifier,
    ColumnPhysicalType.Sql_variant,
    ColumnPhysicalType.Xml,
    ColumnPhysicalType.Binary,
    ColumnPhysicalType.Image,
    ColumnPhysicalType.Varbinary,
    ColumnPhysicalType.Nchar,
    ColumnPhysicalType.Char,
    ColumnPhysicalType.Nvarchar,
    ColumnPhysicalType.Varchar,
    ColumnPhysicalType.Text,
    ColumnPhysicalType.Ntext,
    ColumnPhysicalType.Hierarchyid,
    ColumnPhysicalType.Geometry,
    ColumnPhysicalType.Geography,
    ColumnPhysicalType.Timestamp
];

export const sqlServerBinaryLengthMap = {
    binary: {
        minValue: 1,
        maxValue: 8000,
        supportMaxValue: false
    },
    varbinary: {
        minValue: 1,
        maxValue: 8000,
        supportMaxValue: true
    },
    nchar: {
        minValue: 1,
        maxValue: 4000,
        supportMaxValue: false
    },
    char: {
        minValue: 1,
        maxValue: 8000,
        supportMaxValue: false
    },
    nvarchar: {
        minValue: 1,
        maxValue: 4000,
        supportMaxValue: true
    },
    varchar: {
        minValue: 1,
        maxValue: 8000,
        supportMaxValue: true
    }
};

export const sqlServerPrecisionMap = {
    datetime2: {
        minValue: 0,
        maxValue: 7
    },
    datetimeoffset: {
        minValue: 0,
        maxValue: 7
    },
    decimal: {
        minValue: 1,
        maxValue: 38
    },
    numeric: {
        minValue: 1,
        maxValue: 38
    }
};

export const sqlServerScaleMap = {
    time: {
        minValue: 0,
        maxValue: 7
    },
    decimal: {
        minValue: 0,
        maxValue: 38,
        defaultValue: 18,
        smallerThanPrecision: true
    },
    numeric: {
        minValue: 0,
        maxValue: 38,
        defaultValue: 18,
        smallerThanPrecision: true
    }
};

export const sqlServerDatasetMetadata: IDatasetTypeMetadata = {
    type: adfModelTypeMap.sqlServer.datasetType,
    supportLinkedServiceTypes: [adfModelTypeMap.sqlServer.linkedServiceType],
    displayName: stringResources.SqlServer_DisplayText,
    datasetKind: 'table',
    isSource: true,
    isSink: true,
    icon: {
        className: 'svg-i-100x100-SQLServer'
    },
    supportAutoTableCreation: true,
    supportCreatedAsV2: true,
    supportDataFlowSink: true,
    supportDataFlowSource: true,
    splitTablePath: true,
    isFirstClass: true,
    isDataflowCompatible: true,
    supportDataFlowStoreProcedure: true,
    supportGetMetadataActivity: true,
    category: ['Database'],
    supportParallelRead: true,
    supportDataTypes: defaultSupportedColumnTypes.concat(ColumnType.Date),
    supportEditPhysicalType: __IS_FABRIC_DPE__,
    copySourceControls: [queryTimeoutMetadata].concat([
        isolationLevelMetadata,
        getSqlParallelReadMetadataConsts(adfModelTypeMap.sqlServer.copySourceType)
    ]),
    copySinkControls: [
        getSqlWriteBehavior(true),
        autoTableCreationMetadata(),
        preCopyScriptMetadata(),
        sqlWriteBatchTimeoutMetadata,
        sqlWriteBatchSizeMetadata,
        maxConcurrentConnectionsMetadata
    ],
    LogicalToPhysicalMapping: sqlServerLogicalToPhysicalMapping,
    PhysicalToLogicalMapping: sqlServerPhysicalToLogicalMapping,
    supportedPhysicalTypes: sqlServerSupportedPhysicalTypes,
    binaryPhysicalTypes: sqlBinaryPhysicalTypes,
    binaryLengthMap: sqlServerBinaryLengthMap,
    precisionMap: sqlServerPrecisionMap,
    scaleMap: sqlServerScaleMap,
    copySourcePropertiesRequireParameters: ['sqlReaderQuery', 'partitionOption'],
    copySinkPropertiesRequireParameters: ['preCopyScript', 'tableOption'],
    copyJob: {
        updateMethod: {
            supportUpdateMethod: true,
            options: [getAppendOption(), getUpsertOption(), getSCD2Option()],
            updateMethodProperties: ['writeBehavior', 'upsertSettings'],
            defaultUpdateMethod: UpdateMethod.Append,
            upsertKeyColumnRequired: true
        },
        fullLoadBehavior: {
            supportFullLoadBehavior: true
        },
        multipleQuery: true,
        sourcePartition: {
            supportSourcePartition: true
        }
    }
};
